import React, { createContext, useContext, useMemo, useCallback, useEffect } from "react";
import type { ReactNode } from "react";
import { driver } from "driver.js";
import type { Driver, DriveStep } from "driver.js";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation, useAuthentication } from "@repo/core";
import "driver.js/dist/driver.css";

const TOUR_COMPLETED_KEY = "anp_tour_completed";
const UNAUTHENTICATED_TOUR_COMPLETED_KEY = "anp_unauthenticated_tour_completed";

interface ProductTourContextType {
    startTour: () => void;
    startUnauthenticatedTour: () => void;
}

const ProductTourContext = createContext<ProductTourContextType | undefined>(
    undefined
);

interface ProductTourProviderProps {
    children: ReactNode;
}

export const ProductTourProvider: React.FC<ProductTourProviderProps> = ({
    children,
}) => {
    const { t: tTour } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { user, isLoading: isAuthLoading } = useAuthentication();

    // Helper function to wait for an element to be present in the DOM
    const waitForElement = useCallback(
        (selector: string, timeout = 5000): Promise<Element | null> => {
            return new Promise((resolve) => {
                // Check if element already exists
                const element = document.querySelector(selector);
                if (element) {
                    resolve(element);
                    return;
                }

                // Set up timeout
                const timeoutId = setTimeout(() => {
                    observer.disconnect();
                    resolve(null);
                }, timeout);

                // Set up MutationObserver to watch for element
                const observer = new MutationObserver(() => {
                    const element = document.querySelector(selector);
                    if (element) {
                        clearTimeout(timeoutId);
                        observer.disconnect();
                        resolve(element);
                    }
                });

                observer.observe(document.body, {
                    childList: true,
                    subtree: true,
                });
            });
        },
        []
    );

    // Helper function to check which element is visible (for responsive layouts)
    const getVisibleElement = useCallback((selectors: string[]): Element | null => {
        for (const selector of selectors) {
            const element = document.querySelector(selector);
            if (element) {
                const rect = element.getBoundingClientRect();
                const computedStyle = window.getComputedStyle(element);
                // Check if element is visible (not hidden, has dimensions, and is displayed)
                if (
                    computedStyle.display !== 'none' &&
                    computedStyle.visibility !== 'hidden' &&
                    rect.width > 0 &&
                    rect.height > 0
                ) {
                    return element;
                }
            }
        }
        return null;
    }, []);

    const driverInstance = useMemo<Driver>(() => {
        return driver({
            showProgress: true,
            showButtons: ["next", "previous", "close"],
            nextBtnText: tTour("tour.buttons.next"),
            prevBtnText: tTour("tour.buttons.back"),
            doneBtnText: tTour("tour.buttons.finish"),
            progressText: "{{current}} / {{total}}",
            overlayColor: "rgba(0, 0, 0, 0.5)",
            smoothScroll: true,
        });
    }, [tTour]);

    const startTour = useCallback(() => {
        // Mark tour as completed when it starts
        localStorage.setItem(TOUR_COMPLETED_KEY, "true");

        // Navigate to home page before starting tour
        navigate("/");

        const createSteps = (): DriveStep[] => [
            {
                element: '[data-tour="new-strategy-button"]',
                popover: {
                    title: tTour("tour.steps.newStrategyButton.title"),
                    description: tTour("tour.steps.newStrategyButton.text"),
                    side: "bottom",
                    align: "start",
                    onNextClick: async () => {
                        // Hide overlay during navigation
                        const overlay = document.querySelector('.driver-overlay');
                        const popover = document.querySelector('.driver-popover');
                        if (overlay) (overlay as HTMLElement).style.opacity = '0';
                        if (popover) (popover as HTMLElement).style.opacity = '0';

                        navigate("/factor-mimicking");
                        await waitForElement('[data-tour="performance-chart"]', 5000);
                        await new Promise(resolve => setTimeout(resolve, 100));

                        // Restore visibility and move next
                        if (overlay) (overlay as HTMLElement).style.opacity = '';
                        if (popover) (popover as HTMLElement).style.opacity = '';
                        driverInstance.moveNext();
                    },
                },
            },
            {
                element: '[data-tour="performance-chart"]',
                popover: {
                    title: tTour("tour.steps.performanceChart.title"),
                    description: tTour("tour.steps.performanceChart.text"),
                    side: "top",
                    align: "start",
                    onPrevClick: async () => {
                        // Hide overlay during navigation
                        const overlay = document.querySelector('.driver-overlay');
                        const popover = document.querySelector('.driver-popover');
                        if (overlay) (overlay as HTMLElement).style.opacity = '0';
                        if (popover) (popover as HTMLElement).style.opacity = '0';

                        navigate("/");
                        await waitForElement('[data-tour="new-strategy-button"]', 5000);
                        await new Promise(resolve => setTimeout(resolve, 100));

                        // Restore visibility and move previous
                        if (overlay) (overlay as HTMLElement).style.opacity = '';
                        if (popover) (popover as HTMLElement).style.opacity = '';
                        driverInstance.movePrevious();
                    },
                },
            },
            {
                element: () => {
                    // Check which element is visible (desktop vs mobile)
                    const visibleElement = getVisibleElement([
                        '[data-tour="factor-filters"]',
                        '[data-tour="filter-indicators-button"]'
                    ]);
                    // Fallback to factor-filters if nothing is visible
                    return visibleElement || (document.querySelector('[data-tour="factor-filters"]') || document.body);
                },
                popover: {
                    title: tTour("tour.steps.factorFilters.title"),
                    description: tTour("tour.steps.factorFilters.text"),
                    side: "left",
                    align: "start",
                },
            },
            {
                element: '[data-tour="indicators-table"]',
                popover: {
                    title: tTour("tour.steps.indicatorsTable.title"),
                    description: tTour("tour.steps.indicatorsTable.text"),
                    side: "top",
                    align: "start",
                    onNextClick: async () => {
                        // Hide overlay during navigation
                        const overlay = document.querySelector('.driver-overlay');
                        const popover = document.querySelector('.driver-popover');
                        if (overlay) (overlay as HTMLElement).style.opacity = '0';
                        if (popover) (popover as HTMLElement).style.opacity = '0';

                        navigate("/delivered/sp500/3y/marketcap");
                        await waitForElement('[data-tour="strategy-settings"]', 5000);
                        await new Promise(resolve => setTimeout(resolve, 100));

                        // Restore visibility and move next
                        if (overlay) (overlay as HTMLElement).style.opacity = '';
                        if (popover) (popover as HTMLElement).style.opacity = '';
                        driverInstance.moveNext();
                    },
                },
            },
            {
                element: () => {
                    // Check which element is visible (desktop vs mobile)
                    const visibleElement = getVisibleElement([
                        '[data-tour="strategy-settings"]',
                        '[data-tour="strategy-settings-button"]'
                    ]);
                    // Fallback to strategy-settings if nothing is visible
                    return visibleElement || (document.querySelector('[data-tour="strategy-settings"]') || document.body);
                },
                popover: {
                    title: tTour("tour.steps.strategySettings.title"),
                    description: tTour("tour.steps.strategySettings.text"),
                    side: "left",
                    align: "start",
                    onPrevClick: async () => {
                        // Hide overlay during navigation
                        const overlay = document.querySelector('.driver-overlay');
                        const popover = document.querySelector('.driver-popover');
                        if (overlay) (overlay as HTMLElement).style.opacity = '0';
                        if (popover) (popover as HTMLElement).style.opacity = '0';

                        navigate("/factor-mimicking");
                        await waitForElement('[data-tour="indicators-table"]', 5000);
                        await new Promise(resolve => setTimeout(resolve, 100));

                        // Restore visibility and move previous
                        if (overlay) (overlay as HTMLElement).style.opacity = '';
                        if (popover) (popover as HTMLElement).style.opacity = '';
                        driverInstance.movePrevious();
                    },
                },
            },
            {
                element: '[data-tour="portfolio-constituents"]',
                popover: {
                    title: tTour("tour.steps.portfolioConstituents.title"),
                    description: tTour("tour.steps.portfolioConstituents.text"),
                    side: "top",
                    align: "start",
                },
            },
            {
                element: '[data-tour="drawdowns-chart"]',
                popover: {
                    title: tTour("tour.steps.drawdownsChart.title"),
                    description: tTour("tour.steps.drawdownsChart.text"),
                    side: "top",
                    align: "start",
                },
            },
            {
                element: () => {
                    // Check which element is visible (desktop vs mobile)
                    const visibleElement = getVisibleElement([
                        '[data-tour="add-to-overview-button"]',
                        '[data-tour="add-to-overview-button-mobile"]'
                    ]);
                    // Fallback to add-to-overview-button if nothing is visible
                    return visibleElement || (document.querySelector('[data-tour="add-to-overview-button"]') || document.body);
                },
                popover: {
                    title: tTour("tour.steps.addToOverviewButton.title"),
                    description: tTour("tour.steps.addToOverviewButton.text"),
                    side: "bottom",
                    align: "start",
                },
            },
            {
                popover: {
                    title: tTour("tour.steps.complete.title"),
                    description: tTour("tour.steps.complete.text"),
                },
            },
        ];

        driverInstance.setSteps(createSteps());

        // Small delay to ensure navigation completes
        setTimeout(() => {
            driverInstance.drive();
        }, 100);
    }, [navigate, tTour, driverInstance, waitForElement, getVisibleElement]);

    // Unauthenticated user tour (for first-time visitors)
    const startUnauthenticatedTour = useCallback(async () => {
        // Mark unauthenticated tour as completed when it starts
        localStorage.setItem(UNAUTHENTICATED_TOUR_COMPLETED_KEY, "true");

        // Navigate to factor-mimicking page before starting tour
        navigate("/factor-mimicking");

        // Wait for the indicators table to be present before starting the tour
        await waitForElement('[data-tour="indicators-table"]', 5000);
        await new Promise(resolve => setTimeout(resolve, 200));

        const createUnauthenticatedSteps = (): DriveStep[] => [
            {
                element: '[data-tour="indicators-table"]',
                popover: {
                    title: tTour("tour.unauthenticated.step1.title", "TODO: Add title"),
                    description: tTour("tour.unauthenticated.step1.text", "TODO: Add description"),
                    side: "bottom",
                    align: "start",
                    onNextClick: async () => {
                        navigate("/delivered/sp500/3y/marketcap");
                        await waitForElement('[data-tour="portfolio-constituents"]', 5000);
                        driverInstance.moveNext();
                    },
                },
            },
            {
                element: '[data-tour="portfolio-constituents"]',
                popover: {
                    title: tTour("tour.unauthenticated.step2.title", "TODO: Add title"),
                    description: tTour("tour.unauthenticated.step2.text", "TODO: Add description"),
                    side: "top",
                    align: "start",
                },
            },
            {
                element: () => {
                    // Check which element is visible (desktop vs mobile)
                    const visibleElement = getVisibleElement([
                        '[data-tour="strategy-settings"]',
                        '[data-tour="strategy-settings-button"]'
                    ]);
                    // Fallback to strategy-settings if nothing is visible
                    return visibleElement || (document.querySelector('[data-tour="strategy-settings"]') || document.body);
                },
                popover: {
                    title: tTour("tour.unauthenticated.step3.title", "TODO: Add title"),
                    description: tTour("tour.unauthenticated.step3.text", "TODO: Add description"),
                    side: "left",
                    align: "start",
                },
            },
        ];

        driverInstance.setSteps(createUnauthenticatedSteps());
        driverInstance.drive();
    }, [navigate, tTour, driverInstance, waitForElement, getVisibleElement]);

    // Auto-start tour for first-time unauthenticated users
    useEffect(() => {
        // Check if user is NOT logged in and auth is not loading
        if (user || isAuthLoading) {
            return;
        }

        // Check if unauthenticated tour was already completed
        const unauthTourCompleted = localStorage.getItem(UNAUTHENTICATED_TOUR_COMPLETED_KEY);
        if (unauthTourCompleted === "true") {
            return;
        }

        // Only start tour on factor-mimicking page, so visitors on
        // login/signup/legal pages are not navigated away
        if (location.pathname !== "/factor-mimicking") {
            return;
        }

        // Wait a bit for the page to fully load before starting the tour
        const timer = setTimeout(() => {
            startUnauthenticatedTour();
        }, 1500); // Slightly longer delay for unauthenticated users

        return () => clearTimeout(timer);
    }, [user, isAuthLoading, location.pathname, startUnauthenticatedTour]);

    // Auto-start tour for first-time logged-in users
    useEffect(() => {
        // Check if user is logged in and auth is not loading
        if (!user || isAuthLoading) {
            return;
        }

        // Check if tour was already completed
        const tourCompleted = localStorage.getItem(TOUR_COMPLETED_KEY);
        if (tourCompleted === "true") {
            return;
        }

        // Only start tour on home page
        if (location.pathname !== "/") {
            return;
        }

        // Wait a bit for the page to fully load before starting the tour
        const timer = setTimeout(() => {
            startTour();
        }, 1000);

        return () => clearTimeout(timer);
    }, [user, isAuthLoading, location.pathname, startTour]);

    const contextValue: ProductTourContextType = {
        startTour,
        startUnauthenticatedTour,
    };

    return (
        <ProductTourContext.Provider value={contextValue}>
            {children}
        </ProductTourContext.Provider>
    );
};

export const useProductTour = (): ProductTourContextType => {
    const context = useContext(ProductTourContext);
    if (context === undefined) {
        throw new Error("useProductTour must be used within a ProductTourProvider");
    }
    return context;
};
